import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import { Zap, Clock, Percent, Tag, Plus, Search, RefreshCw, Play, StopCircle, TrendingDown, Users, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PROMO_TYPES, PROMO_TARGETS, CountdownWidget, PromoCard, StatsCard } from './promotionsWidgets';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Main Component
export default function GestionPromotions({ getAuthHeader }) {
  const { t: t } = useTranslation();
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Modal states
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  
  // Form state
  const [promoForm, setPromoForm] = useState({
    nom: '',
    description: '',
    type: 'FLASH',
    cible: 'CREDITS',
    type_reduction: 'PERCENTAGE',
    valeur_reduction: 10,
    code_promo: '',
    date_debut: new Date().toISOString().slice(0, 16),
    date_fin: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    limite_utilisations: null,
    conditions: {},
    actif: true
  });

  const [searchTerm, setSearchTerm] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [promosRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/promotions`, { headers }),
        axios.get(`${BACKEND_URL}/api/promotions/stats`, { headers })
      ]);
      // Trier du plus récent au plus ancien
      const sortedPromos = (promosRes.data || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setPromotions(sortedPromos);
      setStats(statsRes.data);
    } catch {
      // Initialize with empty data if API not ready
      setPromotions([]);
      setStats({
        total_promotions: 0,
        promotions_actives: 0,
        total_utilisations: 0,
        total_economise: 0
      });
    }
    setLoading(false);
  }, [getAuthHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Generate promo code
  const generatePromoCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPromoForm({ ...promoForm, code_promo: code });
  };

  // Save promotion
  const handleSavePromo = async () => {
    if (!promoForm.nom || !promoForm.valeur_reduction) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const headers = getAuthHeader();
      const payload = {
        ...promoForm,
        date_debut: new Date(promoForm.date_debut).toISOString(),
        date_fin: new Date(promoForm.date_fin).toISOString()
      };

      if (editingPromo) {
        await axios.put(`${BACKEND_URL}/api/promotions/${editingPromo.id}`, payload, { headers });
        toast.success('Promotion mise à jour !');
      } else {
        await axios.post(`${BACKEND_URL}/api/promotions`, payload, { headers });
        toast.success('Promotion créée !');
      }

      setShowPromoModal(false);
      setEditingPromo(null);
      resetForm();
      loadData();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Delete promotion
  const handleDeletePromo = async (promo) => {
    if (!window.confirm(`Supprimer la promotion "${promo.nom}" ?`)) return;
    
    try {
      const headers = getAuthHeader();
      await axios.delete(`${BACKEND_URL}/api/promotions/${promo.id}`, { headers });
      toast.success('Promotion supprimée');
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle promotion
  const handleTogglePromo = async (promo) => {
    try {
      const headers = getAuthHeader();
      await axios.patch(`${BACKEND_URL}/api/promotions/${promo.id}/toggle`, {}, { headers });
      toast.success(promo.actif ? 'Promotion désactivée' : 'Promotion activée');
      loadData();
    } catch {
      toast.error('Erreur lors de la modification');
    }
  };

  // Duplicate promotion
  const handleDuplicatePromo = (promo) => {
    setPromoForm({
      ...promo,
      id: undefined,
      nom: `${promo.nom} (copie)`,
      code_promo: '',
      utilisations: 0,
      date_debut: new Date().toISOString().slice(0, 16),
      date_fin: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
    });
    setEditingPromo(null);
    setShowPromoModal(true);
  };

  // Reset form
  const resetForm = () => {
    setPromoForm({
      nom: '',
      description: '',
      type: 'FLASH',
      cible: 'CREDITS',
      type_reduction: 'PERCENTAGE',
      valeur_reduction: 10,
      code_promo: '',
      date_debut: new Date().toISOString().slice(0, 16),
      date_fin: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
      limite_utilisations: null,
      conditions: {},
      actif: true
    });
  };

  // Filter promotions
  const now = new Date();
  const activePromos = promotions.filter(p => p.actif && new Date(p.date_fin) > now && new Date(p.date_debut) <= now);
  const pendingPromos = promotions.filter(p => p.actif && new Date(p.date_debut) > now);
  const expiredPromos = promotions.filter(p => !p.actif || new Date(p.date_fin) <= now);

  const filteredPromos = (list) => {
    if (!searchTerm) return list;
    const search = searchTerm.toLowerCase();
    return list.filter(p => 
      p.nom?.toLowerCase().includes(search) ||
      p.code_promo?.toLowerCase().includes(search)
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-orange-500" />
            Promotions Flash
          </h2>
          <p className="text-gray-500">Gérez vos promotions et offres limitées</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button 
            onClick={() => { resetForm(); setEditingPromo(null); setShowPromoModal(true); }} 
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            <Zap className="w-4 h-4 mr-2" />
            Nouvelle Promo
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={Sparkles}
          title="Promos Actives"
          value={activePromos.length}
          subtitle="En cours"
          color="#EF4444"
        />
        <StatsCard
          icon={Clock}
          title="À Venir"
          value={pendingPromos.length}
          subtitle="Programmées"
          color="#3B82F6"
        />
        <StatsCard
          icon={Users}
          title="Utilisations"
          value={stats?.total_utilisations || 0}
          subtitle="Total"
          color="#10B981"
        />
        <StatsCard
          icon={TrendingDown}
          title="Économies Clients"
          value={`${stats?.total_economise || 0}€`}
          subtitle="Total remisé"
          color="#8B5CF6"
        />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Rechercher une promotion..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Play className="w-4 h-4 text-green-500" />
            En Cours ({activePromos.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-500" />
            À Venir ({pendingPromos.length})
          </TabsTrigger>
          <TabsTrigger value="expired" className="flex items-center gap-2">
            <StopCircle className="w-4 h-4 text-gray-500" />
            Terminées ({expiredPromos.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          {filteredPromos(activePromos).length === 0 ? (
            <Card className="p-12 text-center">
              <Sparkles className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">Aucune promotion active</p>
              <Button onClick={() => { resetForm(); setShowPromoModal(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Créer une promo flash
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPromos(activePromos).map(promo => (
                <PromoCard 
                  key={promo.id} 
                  promo={promo}
                  onEdit={(p) => {
                    setEditingPromo(p);
                    setPromoForm({
                      ...p,
                      date_debut: new Date(p.date_debut).toISOString().slice(0, 16),
                      date_fin: new Date(p.date_fin).toISOString().slice(0, 16)
                    });
                    setShowPromoModal(true);
                  }}
                  onDelete={handleDeletePromo}
                  onToggle={handleTogglePromo}
                  onDuplicate={handleDuplicatePromo}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pending" className="mt-6">
          {filteredPromos(pendingPromos).length === 0 ? (
            <Card className="p-12 text-center">
              <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune promotion programmée</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPromos(pendingPromos).map(promo => (
                <PromoCard 
                  key={promo.id} 
                  promo={promo}
                  onEdit={(p) => {
                    setEditingPromo(p);
                    setPromoForm({
                      ...p,
                      date_debut: new Date(p.date_debut).toISOString().slice(0, 16),
                      date_fin: new Date(p.date_fin).toISOString().slice(0, 16)
                    });
                    setShowPromoModal(true);
                  }}
                  onDelete={handleDeletePromo}
                  onToggle={handleTogglePromo}
                  onDuplicate={handleDuplicatePromo}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="expired" className="mt-6">
          {filteredPromos(expiredPromos).length === 0 ? (
            <Card className="p-12 text-center">
              <StopCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune promotion expirée</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPromos(expiredPromos).map(promo => (
                <PromoCard 
                  key={promo.id} 
                  promo={promo}
                  onEdit={(p) => {
                    setEditingPromo(p);
                    setPromoForm({
                      ...p,
                      date_debut: new Date(p.date_debut).toISOString().slice(0, 16),
                      date_fin: new Date(p.date_fin).toISOString().slice(0, 16)
                    });
                    setShowPromoModal(true);
                  }}
                  onDelete={handleDeletePromo}
                  onToggle={handleTogglePromo}
                  onDuplicate={handleDuplicatePromo}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Promotion Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-500" />
              {editingPromo ? 'Modifier la promotion' : 'Nouvelle promotion flash'}
            </DialogTitle>
            <DialogDescription>
              Configurez les détails de votre promotion
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            {/* Nom */}
            <div className="col-span-2">
              <Label>Nom de la promotion *</Label>
              <Input
                value={promoForm.nom}
                onChange={(e) => setPromoForm({ ...promoForm, nom: e.target.value })}
                placeholder="Ex: Black Friday -50%"
              />
            </div>

            {/* Description */}
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={promoForm.description}
                onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                placeholder="Description de l'offre..."
              />
            </div>

            {/* Type de promo */}
            <div>
              <Label>Type de promotion</Label>
              <Select value={promoForm.type} onValueChange={(v) => setPromoForm({ ...promoForm, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROMO_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" style={{ color: type.color }} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Cible */}
            <div>
              <Label>Applicable sur</Label>
              <Select value={promoForm.cible} onValueChange={(v) => setPromoForm({ ...promoForm, cible: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROMO_TARGETS.map(target => (
                    <SelectItem key={target.value} value={target.value}>
                      <div className="flex items-center gap-2">
                        <target.icon className="w-4 h-4" />
                        {target.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type de réduction */}
            <div>
              <Label>Type de réduction</Label>
              <Select value={promoForm.type_reduction} onValueChange={(v) => setPromoForm({ ...promoForm, type_reduction: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENTAGE">
                    <div className="flex items-center gap-2">
                      <Percent className="w-4 h-4" /> Pourcentage (%)
                    </div>
                  </SelectItem>
                  <SelectItem value="FIXED">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4" /> Montant fixe (€)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Valeur */}
            <div>
              <Label>Valeur de la réduction *</Label>
              <Input
                type="number"
                min="0"
                value={promoForm.valeur_reduction}
                onChange={(e) => setPromoForm({ ...promoForm, valeur_reduction: parseFloat(e.target.value) || 0 })}
              />
            </div>

            {/* Code promo */}
            <div className="col-span-2">
              <Label>Code promo (optionnel)</Label>
              <div className="flex gap-2">
                <Input
                  value={promoForm.code_promo}
                  onChange={(e) => setPromoForm({ ...promoForm, code_promo: e.target.value.toUpperCase() })}
                  placeholder="SUMMER2025"
                  className="font-mono"
                />
                <Button type="button" variant="outline" onClick={generatePromoCode}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Générer
                </Button>
              </div>
            </div>

            {/* Date début */}
            <div>
              <Label>Date de début *</Label>
              <Input
                type="datetime-local"
                value={promoForm.date_debut}
                onChange={(e) => setPromoForm({ ...promoForm, date_debut: e.target.value })}
              />
            </div>

            {/* Date fin */}
            <div>
              <Label>Date de fin *</Label>
              <Input
                type="datetime-local"
                value={promoForm.date_fin}
                onChange={(e) => setPromoForm({ ...promoForm, date_fin: e.target.value })}
              />
            </div>

            {/* Limite utilisations */}
            <div>
              <Label>Limite d'utilisations</Label>
              <Input
                type="number"
                min="0"
                value={promoForm.limite_utilisations || ''}
                onChange={(e) => setPromoForm({ ...promoForm, limite_utilisations: parseInt(e.target.value) || null })}
                placeholder="Illimité"
              />
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <Switch
                checked={promoForm.actif}
                onCheckedChange={(v) => setPromoForm({ ...promoForm, actif: v })}
              />
              <Label>Activer immédiatement</Label>
            </div>

            {/* Preview */}
            {promoForm.date_fin && (
              <div className="col-span-2">
                <Label>Aperçu du compte à rebours</Label>
                <CountdownWidget 
                  endDate={promoForm.date_fin}
                  size="default"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSavePromo} 
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              <Zap className="w-4 h-4 mr-2" />
              {editingPromo ? 'Mettre à jour' : 'Créer la promo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
